import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { contextRevision } from '../../bridge/context';
import type { Project } from '../../domain/schemas';
import { downloadBlob, downloadText } from '../../lib/download';
import { DEFAULT_LONG_EDGE, REVIEW_LONG_EDGES, reviewSize, type CapturePhase, type ReviewLongEdge } from '../../review/capture';
import { MAX_BRIEF_CHARS, reviewDocumentName } from '../../review/document';
import { exportReview, prepareReview, reviewSources, type ReviewExportResult } from '../../review/reviewExport';
import { DEFAULT_REVIEW_FRAMES, MAX_REVIEW_FRAMES, evenTimestamps, formatTime, gridFrameAt, gridFrames, parseCustomTimes } from '../../review/timestamps';
import { useServices } from '../../state/services';
import { Alert, Button, Panel, inputClass } from '../ui';

const sel = 'rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';
const label = 'text-[11px] text-zinc-400';

type RunState =
  | { status: 'idle' }
  | { status: 'running'; phase: CapturePhase; done: number; total: number; revision: string }
  | { status: 'done'; result: ReviewExportResult; urls: string[]; json: string; revision: string; ms: number }
  | { status: 'cancelled' }
  | { status: 'error'; message: string };

const mb = (bytes: number) => (bytes < 1024 * 1024 ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${(bytes / 1048576).toFixed(1)} MB`);
const seconds = (ms: number) => `${(ms / 1000).toFixed(1)} s`;

/**
 * Export for Visual Review (Phase 5C.1): renders chosen moments of a scene with the
 * video-export renderer and offers the PNG frames and review.json as downloads, for the
 * owner to give to Claude, ChatGPT or another assistant. Nothing is uploaded or sent.
 */
export function VisualReviewPanel({ project, runExport = exportReview }: { project: Project; /** Test seam. */ runExport?: typeof exportReview }) {
  const { images, analysis } = useServices();
  const id = useId();
  const [sceneId, setSceneId] = useState(project.activeSceneId);
  const scene = project.scenes.find((s) => s.id === sceneId) ?? project.scenes[0]!;
  const sources = useMemo(() => reviewSources(project, scene.id), [project, scene.id]);
  const [sourceKey, setSourceKey] = useState<string | null>(null);
  const source = sources.find((s) => s.key === sourceKey) ?? sources[0] ?? null;
  const [mode, setMode] = useState<'even' | 'custom'>('even');
  const [count, setCount] = useState(DEFAULT_REVIEW_FRAMES);
  const [custom, setCustom] = useState('');
  const [longEdge, setLongEdge] = useState<ReviewLongEdge>(DEFAULT_LONG_EDGE);
  const [goal, setGoal] = useState('');
  const [constraints, setConstraints] = useState('');
  const [includeNames, setIncludeNames] = useState(false);
  const [run, setRun] = useState<RunState>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);
  // Thumbnail URLs of the result on screen; nothing else owns object URLs here.
  const urlsRef = useRef<string[]>([]);
  // Request identity: only the latest export of a mounted panel may update state or
  // create URLs. Starting another export, cancelling and unmounting all retire it.
  const requestRef = useRef(0);
  const mountedRef = useRef(false);

  const revokeUrls = () => {
    for (const u of urlsRef.current) URL.revokeObjectURL(u);
    urlsRef.current = [];
  };
  // Leaving the workspace cancels a running export, retires it and releases the thumbnails.
  useEffect(() => {
    const requests = requestRef;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requests.current++;
      abortRef.current?.abort();
      abortRef.current = null;
      revokeUrls();
    };
  }, []);

  const maxCount = source ? Math.min(MAX_REVIEW_FRAMES, gridFrames(source.grid)) : MAX_REVIEW_FRAMES;
  const plan = useMemo((): { timestamps: number[]; error: null } | { timestamps: null; error: string } => {
    if (!source) return { timestamps: null, error: 'This scene has no animation or sync session yet.' };
    try {
      return { timestamps: mode === 'even' ? evenTimestamps(source.grid, Math.min(count, maxCount)) : parseCustomTimes(custom, source.grid), error: null };
    } catch (e) {
      return { timestamps: null, error: e instanceof Error ? e.message : String(e) };
    }
  }, [source, mode, count, custom, maxCount]);
  const size = reviewSize(scene.width, scene.height, longEdge);
  const currentRevision = useMemo(() => contextRevision(project), [project]);
  const running = run.status === 'running';

  const start = async () => {
    if (!source || !plan.timestamps || abortRef.current) return;
    const request = ++requestRef.current;
    const current = () => mountedRef.current && requestRef.current === request;
    revokeUrls();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const revision = currentRevision;
    setRun({ status: 'running', phase: 'preparing', done: 0, total: plan.timestamps.length, revision });
    const t0 = performance.now();
    try {
      const prepared = prepareReview(project, source.choice);
      const session = prepared.source.kind === 'sync-session' ? prepared.source.session : null;
      const result = await runExport({
        prepared,
        timestamps: plan.timestamps,
        selection: mode,
        longEdge,
        brief: { goal, constraints },
        includeNames,
        loadImage: (assetId) => images.load(assetId),
        features: session ? analysis.peek(prepared.snapshot.project, session) : null,
        signal: ctrl.signal,
        onProgress: (p) => {
          if (current() && !ctrl.signal.aborted) setRun({ status: 'running', ...p, revision });
        },
      });
      // A retired or cancelled request publishes nothing and creates no URL, even if it
      // finished after the last cancellation check.
      if (!current() || ctrl.signal.aborted) return;
      const urls = result.frames.map((f) => URL.createObjectURL(f.blob));
      urlsRef.current = urls;
      setRun({ status: 'done', result, urls, json: JSON.stringify(result.document, null, 2), revision: result.document.target.contextRevision, ms: performance.now() - t0 });
    } catch (e) {
      if (!current() || ctrl.signal.aborted) return;
      setRun({ status: 'error', message: e instanceof Error ? e.message : 'The frames could not be rendered.' });
    } finally {
      if (abortRef.current === ctrl) abortRef.current = null;
    }
  };

  const cancel = () => {
    const ctrl = abortRef.current;
    if (!ctrl) return;
    // Retire the request at once: the renderer is released by the export's own cleanup,
    // and whatever it still returns is dropped.
    requestRef.current++;
    abortRef.current = null;
    ctrl.abort();
    setRun({ status: 'cancelled' });
  };

  return (
    <Panel title="Export for Visual Review">
      <div className="flex flex-col gap-3" data-testid="visual-review">
        <p className="text-xs text-zinc-400">
          Renders chosen moments of a scene exactly as a video export would, as PNG frames plus a <code>review.json</code> that
          describes them. Download them and give them to your assistant yourself: nothing is uploaded or sent anywhere.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${id}-scene`} className={label}>
              Scene
            </label>
            <select
              id={`${id}-scene`}
              className={sel}
              value={scene.id}
              disabled={running}
              onChange={(e) => {
                setSceneId(e.target.value);
                setSourceKey(null);
              }}
              data-testid="review-scene"
            >
              {project.scenes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${id}-source`} className={label}>
              Timeline
            </label>
            <select id={`${id}-source`} className={sel} value={source?.key ?? ''} disabled={running || sources.length === 0} onChange={(e) => setSourceKey(e.target.value)} data-testid="review-source">
              {sources.length === 0 && <option value="">No animation or sync session</option>}
              {sources.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label} ({formatTime(s.grid.durationSec)})
                </option>
              ))}
            </select>
          </div>
        </div>

        {sources.length === 0 && (
          <p className="text-xs text-amber-300" role="note">
            Add an animation to this scene (Animation workspace) or a sync session (Sync workspace) to export review frames.
          </p>
        )}

        <fieldset className="flex flex-col gap-2" disabled={running || !source}>
          <legend className={`${label} mb-1`}>Moments</legend>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5">
              <input type="radio" name={`${id}-mode`} checked={mode === 'even'} onChange={() => setMode('even')} data-testid="review-mode-even" />
              Evenly spaced
            </label>
            <label className="flex items-center gap-1.5">
              <input type="radio" name={`${id}-mode`} checked={mode === 'custom'} onChange={() => setMode('custom')} data-testid="review-mode-custom" />
              Exact times
            </label>
          </div>
          {mode === 'even' ? (
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${id}-count`} className={label}>
                Frames (1–{maxCount})
              </label>
              <select id={`${id}-count`} className={`${sel} w-24`} value={Math.min(count, maxCount)} onChange={(e) => setCount(Number(e.target.value))} data-testid="review-count">
                {Array.from({ length: maxCount }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${id}-times`} className={label}>
                Times in seconds, up to {MAX_REVIEW_FRAMES} (for example: 0.5, 2.75, 1:04)
              </label>
              <input id={`${id}-times`} className={inputClass} value={custom} onChange={(e) => setCustom(e.target.value)} aria-invalid={!!plan.error || undefined} aria-describedby={`${id}-plan`} data-testid="review-times" />
            </div>
          )}
          <div id={`${id}-plan`} aria-live="polite">
            {plan.error ? (
              <p className="text-xs text-red-300" data-testid="review-plan-error">
                {plan.error}
              </p>
            ) : (
              <ol className="flex flex-wrap gap-1.5" aria-label="Frames to render" data-testid="review-plan">
                {(plan.timestamps ?? []).map((t, i) => (
                  <li key={t} className="rounded bg-zinc-800 px-2 py-0.5 font-mono text-[11px] text-zinc-300" data-seconds={t}>
                    f{String(i + 1).padStart(2, '0')} · {formatTime(t)} · frame {gridFrameAt(t, source!.grid.fps)}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </fieldset>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-0.5">
            <label htmlFor={`${id}-size`} className={label}>
              Size
            </label>
            <select id={`${id}-size`} className={sel} value={longEdge} disabled={running} onChange={(e) => setLongEdge(Number(e.target.value) as ReviewLongEdge)} data-testid="review-size">
              {REVIEW_LONG_EDGES.map((edge) => {
                const s = reviewSize(scene.width, scene.height, edge);
                return (
                  <option key={edge} value={edge}>
                    {s.width} × {s.height}
                    {edge === DEFAULT_LONG_EDGE ? ' (recommended)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          <label className="flex items-start gap-1.5 self-end text-sm">
            <input type="checkbox" className="mt-1" checked={includeNames} disabled={running} onChange={(e) => setIncludeNames(e.target.checked)} data-testid="review-names" />
            <span>
              Include names and on-screen text
              <span className="block text-xs text-zinc-400">Scene, layer, object and character names you typed. Off: only ids and types.</span>
            </span>
          </label>
        </div>

        <details className="rounded-md border border-zinc-800 p-2">
          <summary className="cursor-pointer text-sm text-zinc-300">Creative brief (optional)</summary>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${id}-goal`} className={label}>
                What should the scene achieve? ({goal.length}/{MAX_BRIEF_CHARS})
              </label>
              <textarea id={`${id}-goal`} rows={3} maxLength={MAX_BRIEF_CHARS} className={inputClass} value={goal} disabled={running} onChange={(e) => setGoal(e.target.value)} data-testid="review-goal" />
            </div>
            <div className="flex flex-col gap-0.5">
              <label htmlFor={`${id}-constraints`} className={label}>
                What must not change? ({constraints.length}/{MAX_BRIEF_CHARS})
              </label>
              <textarea id={`${id}-constraints`} rows={3} maxLength={MAX_BRIEF_CHARS} className={inputClass} value={constraints} disabled={running} onChange={(e) => setConstraints(e.target.value)} data-testid="review-constraints" />
            </div>
          </div>
        </details>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="primary" onClick={() => void start()} disabled={running || !plan.timestamps} data-testid="review-render">
            Render {plan.timestamps ? `${plan.timestamps.length} frame${plan.timestamps.length === 1 ? '' : 's'}` : 'frames'} ({size.width} × {size.height})
          </Button>
          {running && (
            <Button onClick={cancel} data-testid="review-cancel">
              Cancel
            </Button>
          )}
        </div>

        <div aria-live="polite" data-testid="review-status" data-status={run.status}>
          {run.status === 'running' && (
            <div className="flex flex-col gap-1">
              {currentRevision !== run.revision && (
                <p className="text-xs text-amber-300" data-testid="review-running-stale">
                  The project changed after rendering started. These frames will show the earlier version.
                </p>
              )}
              <progress className="w-full" max={run.total * 2} value={run.done * 2 + (run.phase === 'encoding' ? 1 : 0)} aria-label="Review export progress" />
              <p className="text-xs text-zinc-400">
                {run.phase === 'preparing' ? 'Preparing the renderer and images…' : `${run.phase === 'rendering' ? 'Rendering' : 'Encoding'} frame ${Math.min(run.done + 1, run.total)} of ${run.total}…`}
              </p>
            </div>
          )}
          {run.status === 'cancelled' && <p className="text-xs text-zinc-400">Cancelled. Nothing was kept.</p>}
          {run.status === 'error' && <Alert title="The review frames could not be exported.">{run.message}</Alert>}
        </div>

        {run.status === 'done' && (
          <div className="flex flex-col gap-3" data-testid="review-result" data-review-id={run.result.document.reviewId} data-create-ms={Math.round(run.result.createMs)} data-stale={currentRevision !== run.revision}>
            <p className="text-xs text-zinc-300">
              {run.result.frames.length} frame{run.result.frames.length === 1 ? '' : 's'} · {mb(run.result.totalBytes)} · rendering{' '}
              {seconds(run.result.frames.reduce((n, f) => n + f.renderMs, 0))}, PNG encoding {seconds(run.result.frames.reduce((n, f) => n + f.encodeMs, 0))}, total {seconds(run.ms)}
            </p>
            {currentRevision !== run.revision && (
              <Alert tone="warning" title="The project changed after these frames were rendered.">
                They still show the earlier version. Render again before asking for changes, so a package written from them matches the project.
              </Alert>
            )}
            <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Rendered frames">
              {run.result.frames.map((f, i) => {
                const doc = run.result.document.frames[i]!;
                return (
                  <li key={f.id} className="flex flex-col gap-1" data-testid="review-frame" data-render-ms={f.renderMs.toFixed(2)} data-encode-ms={f.encodeMs.toFixed(2)} data-bytes={f.bytes}>
                    <img src={run.urls[i]} alt={`Frame ${f.id} at ${formatTime(f.seconds)}`} className="w-full rounded border border-zinc-800" />
                    <Button size="sm" onClick={() => downloadBlob(doc.file, f.blob)} data-testid="review-download-frame">
                      {doc.file.replace(/^review-[0-9a-f]+-/, '')} · {mb(f.bytes)}
                    </Button>
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" onClick={() => downloadText(reviewDocumentName(run.result.document.reviewId), run.json)} data-testid="review-download-json">
                Download review.json
              </Button>
              <Button
                onClick={() => {
                  downloadText(reviewDocumentName(run.result.document.reviewId), run.json);
                  run.result.frames.forEach((f, i) => downloadBlob(run.result.document.frames[i]!.file, f.blob));
                }}
                data-testid="review-download-all"
              >
                Download all ({run.result.frames.length + 1} files)
              </Button>
            </div>
            <p className="text-xs text-zinc-400">
              Upload the PNG files and the JSON file to your assistant together (the browser may ask once to allow several
              downloads). Ask it to review the frames against the brief and, if you want changes, to answer with a creation
              package for the <strong>Creation package</strong> box above.
            </p>
          </div>
        )}
      </div>
    </Panel>
  );
}
